import moment from 'moment';
import { storageDateFormat, createDateObject, copyParty, objectSet } from '@/core';
import { order, subOrder } from '@/dataStructures';
import initialState from '@/modules/newOrder/store/newOrder.state';
import deepCopy from 'deepcopy';
import { subOrdersInitialValidationState, validateSubOrders } from '@/modules/newOrder/validation/subOrders';
import { validateOrder } from '@/modules/newOrder/validation/order';

function setNewOrder(state, value) {
	state.order.id = value.id;
	state.order.orderNumber = value.orderNumber;
	state.order.clerk = value.clerk;
	state.dirty = false;
	state.orderClone = { ...deepCopy(state.order), subOrders: [] };
	state.orderShadowCopy = { ...deepCopy(state.order), subOrders: [] };
}


function setEditOrder(state, response) {
	// adding order from datastructures to make sure it has all keys
	// properties not present upon instantiation are not reactive in Vue
	state.order = {
		...order(),
		...response,
	};
	state.orderClone = deepCopy(response);
	state.orderShadowCopy = deepCopy(response);
	state.dirty = false;

	if (response.subOrders) {
		let subOrders = response.subOrders.map(
			s => ({
				...subOrder(),
				...s,
			})
		);
		subOrders = sortSubOrders(subOrders);
		state.subOrders = subOrders;

		if (state.currentOrderTabIndex >= 0) {

			state.selectedSubOrder = state.subOrders[state.currentOrderTabIndex];

			let copy = state.orderShadowCopy.subOrders.find(x => x.id === state.selectedSubOrder.id);
			if (copy) {
				state.selectedSubOrderShadowCopy = copy;
			}
		}

	} else {
		state.subOrders = [];
	}
	state.errors.subOrders = subOrdersInitialValidationState(state.subOrders);
}

function sortSubOrders(subOrders) {
	let i = 0;
	let sortOrder = {};
	let toCheck = subOrders.filter(x => x.prev.length === 0);
	toCheck.sort((a,b) => {
		let aDate = a.etd && a.etd.date ? Number(a.etd.date) : Infinity;
		let bDate = b.etd && b.etd.date ? Number(b.etd.date) : Infinity;
		return bDate - aDate;
	});
	let lookup = subOrders.reduce((acc,c) => {
		acc[c.id] = c;
		return acc;
	}, {});
	while (toCheck.length > 0) {
		let subOrder = toCheck.pop();
		sortOrder[subOrder.id] = i++;
		let nexts = subOrder.next.map(x => lookup[x]);
		toCheck.push(...nexts);
	}
	subOrders.sort((a,b) => sortOrder[a.id] - sortOrder[b.id]);
	return subOrders;
}

function setValue(state, { key, value, resetError }) {
	if (state.order[key] === value) return;
	state.dirty = true;
	state.order[key] = value;
	state.orderShadowCopy[key] = value;
	if (resetError)
		state.errors[resetError] = false;
}

function setSubOrderValue(state, { key, value }) {
	if (state.selectedSubOrder[key] === value) return;
	state.dirty = true;
	state.selectedSubOrder[key] = value;
	state.selectedSubOrderShadowCopy[key] = value;
}

function setCustomer(state, { customer }) {
	state.dirty = true;
	state.order.customer = customer;
	if (customer && customer.currency)
		state.order.invoiceCurrency = customer.currency;
	else {
		let currency = '';
		if (state.order.priceLines) {
			for (let i = 0; i < state.order.priceLines.length; i++) {
				let p = state.order.priceLines[i];
				if (p && p.currency) {
					currency = p;
					break;
				}
			}
		}
		state.order.invoiceCurrency = currency;
	}
	state.orderShadowCopy.customer = state.order.customer;
	state.orderShadowCopy.invoiceCurrency = state.order.invoiceCurrency;
}

function setOrderConsignor(state, party) {
	state.dirty = true;
	state.order.consignor = copyParty(party);
	state.orderShadowCopy.consignor = state.order.consignor;

	if (state.subOrders && state.subOrders.length > 0) {
		state.subOrders.forEach( x => {
			x.consignor = copyParty(party);
		});
	}

	if (state.orderShadowCopy.subOrders && state.orderShadowCopy.subOrders.length > 0) {
		state.orderShadowCopy.subOrders.forEach(x => {
			x.consignor = copyParty(party);
		});
	}
}
function setOrderConsignee(state, party) {
	state.dirty = true;
	state.order.consignee = copyParty(party);
	state.orderShadowCopy.consignee = state.order.consignee;

	if (state.subOrders && state.subOrders.length > 0) {
		state.subOrders.forEach( x => {
			x.consignee = copyParty(party);
		});
	}

	if (state.orderShadowCopy.subOrders && state.orderShadowCopy.subOrders.length > 0) {
		state.orderShadowCopy.subOrders.forEach(x => {
			x.consignee = copyParty(party);
		});
	}
}

function setOrderParty(state, { key, party }) {
	state.dirty = true;
	state.order[key] = copyParty(party);
	state.orderShadowCopy[key] = state.order[key];
}

function setOrderDespatchParty(state, party) {
	state.dirty = true;
	state.order.despatchParty = copyParty(party);
	state.orderShadowCopy.despatchParty = state.order.despatchParty;

	if (state.subOrders.length) {

		state.subOrders[0].despatchParty = state.order.despatchParty;
		state.orderShadowCopy.subOrders[0].despatchParty = state.order.despatchParty;
	}
}

function setOrderDeliveryParty(state, party) {
	state.dirty = true;
	state.order.deliveryParty = copyParty(party);
	state.orderShadowCopy.deliveryParty = state.order.deliveryParty;

	if (state.subOrders.length) {
		let index = state.subOrders.length - 1;

		state.subOrders[index].deliveryParty = state.order.deliveryParty;
		state.orderShadowCopy.subOrders[index].deliveryParty = state.order.deliveryParty;
	}
}

function setSubOrderParty(state, { key, party }) {
	state.dirty = true;
	state.selectedSubOrder[key] = copyParty(party);
	state.selectedSubOrderShadowCopy[key] = state.selectedSubOrder[key];
}

function setSubOrderDespatchParty(state, party) {
	state.dirty = true;
	state.selectedSubOrder.despatchParty = copyParty(party);
	state.selectedSubOrderShadowCopy.despatchParty = state.selectedSubOrder.despatchParty;

	let subOrderIndex = state.subOrders
		.findIndex( subOrder => subOrder === state.selectedSubOrder);

	if (subOrderIndex === -1)
		throw new Error('subOrderIndex === -1, inconsistent data');

	else if (subOrderIndex === 0) {

		state.order.despatchParty = state.selectedSubOrder.despatchParty;
		state.orderShadowCopy.despatchParty = state.selectedSubOrder.despatchParty;
	}
}

function setSubOrderDeliveryParty(state, party) {
	state.dirty = true;
	state.order.deliveryParty = copyParty(party);
	state.selectedSubOrder.deliveryParty = copyParty(party);
	state.selectedSubOrderShadowCopy.deliveryParty = state.selectedSubOrder.deliveryParty;

	let subOrderIndex = state.subOrders
		.findIndex( subOrder => subOrder === state.selectedSubOrder);

	if (subOrderIndex === -1)
		throw new Error('subOrderIndex === -1, inconsistent data');

	else if (subOrderIndex === state.subOrders.length - 1) {

		state.order.deliveryParty = state.selectedSubOrder.deliveryParty;
		state.orderShadowCopy.deliveryParty = state.selectedSubOrder.deliveryParty;
	}
}

function setSubOrderCustomer(state, customer) {
	state.dirty = true;
	state.selectedSubOrder.customer = customer;
	state.selectedSubOrderShadowCopy.customer = state.selectedSubOrder.customer;
}


function cleanState(state, currentClerk) {
	Object.assign(state, initialState());
	state.order.clerk = currentClerk;
	state.orderShadowCopy = { ...deepCopy(state.order), subOrders: [] };
	state.ediName = '';
}

function createNewDateObject(oldDateObject, dateValue, timeValue, hasTime) {

	let oldDateObjectExists = Boolean(oldDateObject);

	let storedDate = oldDateObjectExists
		? oldDateObject.date
		: undefined;

	let date = dateValue || storedDate;

	let time = timeValue ?
		moment(timeValue, 'LT').format('HH:mm') :
		moment(storedDate, storageDateFormat).format('HH:mm');

	let storedHasTime = oldDateObjectExists
		? oldDateObject.hasTime
		: undefined;

	if (hasTime === 'dont-change' && storedHasTime === undefined) {
		hasTime = false;

	} else if (hasTime === 'dont-change' || hasTime === null)
		hasTime = storedHasTime;

	return createDateObject({ date, hasTime, time }, Boolean(dateValue));
}

function setDate(state, { key, dateValue, timeValue, hasTime }) {
	state.dirty = true;

	let oldDateObject = state.order[key];

	state.order[key] =
		createNewDateObject(oldDateObject, dateValue, timeValue, hasTime);

	if (dateValue === '' && (key === 'atd' || key === 'ata')) // You must be able to reset ATD and ATA
		state.order[key].date = null;

	state.orderShadowCopy[key] = state.order[key];
}

function setSubOrderDate(state, { key, dateValue, timeValue, hasTime }) {
	state.dirty = true;
	let oldDateObject = deepCopy(state.selectedSubOrder[key]);

	state.selectedSubOrder[key] =
		createNewDateObject(oldDateObject, dateValue, timeValue, hasTime);

	if (dateValue === '' && (key === 'atd' || key === 'ata')) // You must be able to reset ATD and ATA
		state.selectedSubOrder[key].date = null;

	state.selectedSubOrderShadowCopy[key] = state.selectedSubOrder[key];
}

function setFreeText(state, { key, freeText }) {
	state.dirty = true;
	state.order[key] = freeText;
	state.orderShadowCopy[key] = freeText;
}

function setSubOrderFreeText(state, { key, freeText }) {
	state.dirty = true;
	state.selectedSubOrder[key] = freeText;
	state.selectedSubOrderShadowCopy[key] = freeText;
}

function setServerErrors(state, errors) {
	state.errors.order.etd = errors.invalidEtd && 'invalidDateObject';
	state.errors.order.eta = errors.invalidEta && 'invalidDateObject';
	state.errors.invalidFromAddress = errors.invalidFromAddress || false;
	state.errors.invalidToAddress = errors.invalidToAddress || false;
	state.errors.invalidTransporter = errors.invalidTransporter || false;
}

function setIsLoadingTrip(state, value) {
	state.isLoadingTrip = value;
}

function addAdditionalReference(state, code) {
	state.dirty = true;
	state.order.additionalReferences.push({ code, value: '' });
	state.orderShadowCopy.additionalReferences = state.order.additionalReferences;
}

function setAdditionalReference(state, { code, index, value }) {

	if ( !code && !index ) {
		console.error('Error in setAdditionalReference, either code or index is required');
		return;
	}
	state.dirty = true;

	let newAdditionalReferences = state.order.additionalReferences.slice();
	let additionalReference;

	if (index)
		additionalReference = newAdditionalReferences[index];

	else if (code)
		additionalReference = newAdditionalReferences.find(
			reference => reference.code === code
		);

	additionalReference.value = value;

	state.order.additionalReferences = newAdditionalReferences;
	state.orderShadowCopy.additionalReferences = state.order.additionalReferences;
}

function setFreightLinesValue(state, { lineNo, key, value }) {
	state.dirty = true;
	let newFreightLines = state.order.freightLines.slice();
	let freightLine = newFreightLines.find(line => line.lineNo === lineNo);
	freightLine[key] = value;

	if (key === 'length' || key === 'width' || key === 'height')
		freightLine.volume = freightLine.length * freightLine.width * freightLine.height;

	if (key === 'numberOfUnits') {
		let quantity = 0;
		for (let line of newFreightLines) {
			quantity += line.numberOfUnits;
		}
		state.order.numberOfPackages = quantity;
		state.orderShadowCopy.numberOfPackages = quantity;
	}

	if (key === 'numberOfUnits' || key === 'palletPriceFactorPrice') {

		if (
			freightLine.numberOfUnits
			&& freightLine.palletPriceFactorPrice
		) {
			freightLine.palletPlacePrice
				= freightLine.numberOfUnits
				* freightLine.palletPriceFactorPrice;
		}
	}

	state.order.freightLines = newFreightLines;
	state.orderShadowCopy.freightLines = state.order.freightLines;
}

function addFreightLine(state, freightLine) {
	state.dirty = true;
	let newFreightLines = state.order.freightLines.slice();

	newFreightLines.push(freightLine);

	state.order.freightLines = newFreightLines;
	state.orderShadowCopy.freightLines = state.order.freightLines;
}

function deleteFreightLine(state, lineNo) {
	state.dirty = true;
	let newFreightLines = state.order.freightLines.slice();
	let index = newFreightLines.indexOf( newFreightLines.find( line => line.lineNo === lineNo ) );
	newFreightLines.splice( index, 1 );

	let quantity = 0;
	newFreightLines.forEach((line, index) => {
		line.lineNo = index + 1;
		quantity += line.numberOfUnits;
	});

	state.order.numberOfPackages = quantity;
	state.orderShadowCopy.numberOfPackages = quantity;
	state.order.freightLines = newFreightLines;
	state.orderShadowCopy.freightLines = state.order.freightLines;
}

function validate(state) {
	state.errors.order = validateOrder(state.order);
	state.errors.subOrders = validateSubOrders(state.subOrders);
}

function setSelectedSubOrder(state, subOrderId) {
	let subOrder = state.subOrders.find(x => x.id === subOrderId);
	//Do not set as dirty!
	state.selectedSubOrder = subOrder;

	let copy = state.orderShadowCopy.subOrders.find(x => x.id === subOrderId);
	if (copy) {
		state.selectedSubOrderShadowCopy = copy;
	}
}

function setSelectedSubOrderTrip(state, trip) {
	//Do not set as dirty!
	state.selectedSubOrderTrip = trip;
}

function addTrip(state, trip) {
	//Do not set as dirty!
	state.trips.push(trip);
}

function setCurrentOrderTabIndex(state, index) {
	//Do not set as dirty!
	state.currentOrderTabIndex = index;
}

function setSocket(state, socket) {
	// Not dirty.
	state.socket = socket;
}

function setDeviationBindings(state, deviationBindings) {
	// No dirt - Deviations take care of updating themselves, this is only to sync frontend.
	state.order.deviationBindings = deviationBindings;
}

function autoRefreshOrder(state, patches) {
	for (let p of patches) {
		objectSet(state.orderClone, p.path.substring(1), p.value);
		objectSet(state.orderShadowCopy, p.path.substring(1), p.value);

		let path = p.path.split('/').slice(1);
		if (path[0] === 'data') {
			path.shift();
			let newPath = path.join('.');
			objectSet(state.order, newPath, p.value);
		} else if (path[0] === 'subOrders') {
			let subOrder = state.subOrders.find(x => x.id === path[1]);
			let newPath = path.slice(3).join('.'); // Removes subOrders/id/data from path
			objectSet(subOrder, newPath, p.value);
		}
	}
	if (state.selectedSubOrder) {
		setSelectedSubOrder(state, state.selectedSubOrder.id);
	}
}

function setOrderChangelog(state, changelog) {
	state.changelog = changelog;
}

function setEdiLogs(state, ediLogs) {
	state.ediLog = ediLogs;
}

function set(state, fn) {
	if (fn)
		fn(state);
}

function setHasOpenedPackageLabels(state, hasOpenedPackageLabels) {
	state.dirty = true;
	state.orderShadowCopy.hasOpenedPackageLabels = hasOpenedPackageLabels;
}

export default {
	set,
	cleanState,
	setValue,
	setSubOrderValue,
	setOrderConsignor,
	setOrderConsignee,
	setOrderDespatchParty,
	setOrderDeliveryParty,
	setOrderParty,
	setSubOrderParty,
	setSubOrderDespatchParty,
	setSubOrderDeliveryParty,
	setDate,
	setCustomer,
	setSubOrderCustomer,
	setSubOrderDate,
	addTrip,
	setNewOrder,
	setEditOrder,
	setFreeText,
	setSubOrderFreeText,
	setServerErrors,
	setIsLoadingTrip,
	addAdditionalReference,
	setAdditionalReference,
	setFreightLinesValue,
	addFreightLine,
	deleteFreightLine,
	validate,
	setSelectedSubOrder,
	setSelectedSubOrderTrip,
	setCurrentOrderTabIndex,
	setSocket,
	setDeviationBindings,
	autoRefreshOrder,
	setOrderChangelog,
	setEdiLogs,
	setHasOpenedPackageLabels,
};